import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import "./tour.css";

const PAD = 6;
const GAP = 12;

/** Un elemento fijo (barra inferior, topbar) no se mueve con el scroll: no hay que desplazar la página hacia él. */
const isFixed = (el) => {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    const pos = getComputedStyle(n).position;
    if (pos === "fixed" || pos === "sticky") return true;
  }
  return false;
};

/**
 * Recorrido guiado: oscurece la pantalla, deja un "agujero" sobre el elemento del
 * paso (data-tour="…") y muestra una tarjeta que lo explica. En móvil la tarjeta
 * va abajo (pulgar); en escritorio se ancla al elemento. Teclado: ← → Esc.
 *
 * No sabe de pestañas ni de contenido: recibe `step` ya resuelto y el elemento
 * destino (o null → tarjeta centrada sin spotlight).
 */
export default function Tour({ step, target, index, total, onNext, onPrev, onClose, onSkip, onSkipAll }) {
  const cardRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Medir el destino; re-medir en scroll/resize (el usuario puede desplazar la página).
  useLayoutEffect(() => {
    if (!target) { setRect(null); return; }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const r = target.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    // Traer el elemento a la vista dejando espacio para la tarjeta de abajo en móvil.
    const margin = isMobile ? 260 : 120;
    const r0 = target.getBoundingClientRect();
    const fits = isFixed(target) || (r0.top >= 100 && r0.bottom <= window.innerHeight - margin);
    if (!fits) {
      const y = window.scrollY + r0.top - (isMobile ? 104 : Math.max(104, (window.innerHeight - margin - r0.height) / 2));
      window.scrollTo({ top: Math.max(0, y), behavior: reduced ? "auto" : "smooth" });
    }
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    const t = setTimeout(measure, fits || reduced ? 0 : 320);
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); window.removeEventListener("scroll", schedule, true); window.removeEventListener("resize", schedule); };
  }, [target, isMobile, reduced]);

  // Posicionar la tarjeta (solo escritorio): debajo si cabe, si no arriba, si no a un lado.
  useLayoutEffect(() => {
    if (isMobile || !rect || !cardRef.current) { setCardPos(null); return; }
    const card = cardRef.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const clampX = (x) => Math.min(Math.max(12, x), vw - card.width - 12);
    let top, left;
    if (rect.top + rect.height + GAP + card.height <= vh - 12) {
      top = rect.top + rect.height + GAP; left = clampX(rect.left);
    } else if (rect.top - GAP - card.height >= 12) {
      top = rect.top - GAP - card.height; left = clampX(rect.left);
    } else if (rect.left + rect.width + GAP + card.width <= vw - 12) {
      top = Math.min(Math.max(12, rect.top), vh - card.height - 12); left = rect.left + rect.width + GAP;
    } else {
      top = Math.min(Math.max(12, rect.top), vh - card.height - 12); left = clampX(rect.left - GAP - card.width);
    }
    setCardPos({ top, left });
  }, [rect, isMobile, step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === "Enter") onNext();
      else if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNext, onPrev]);

  const last = index === total - 1;
  const centered = !rect;
  // Móvil: la tarjeta va abajo (pulgar), salvo que el elemento esté en la mitad inferior
  // (p. ej. la barra de navegación): entonces arriba, para no taparlo.
  const cardAtTop = isMobile && rect && rect.top + rect.height / 2 > window.innerHeight * 0.55;

  return createPortal(
    <div className={`tour-root ${isMobile ? "is-mobile" : ""} ${reduced ? "reduced" : ""}`} role="dialog" aria-modal="true" aria-label="Recorrido guiado">
      {centered ? <div className="tour-scrim" /> : (
        <div className="tour-hole" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
      )}
      <div
        ref={cardRef}
        className={`tour-card ${centered ? "is-centered" : ""} ${cardAtTop ? "at-top" : ""}`}
        style={!isMobile && cardPos && !centered ? { top: cardPos.top, left: cardPos.left } : undefined}
      >
        <div className="tour-card-head">
          <span className="tour-progress">{index + 1} de {total}</span>
          <button type="button" className="tour-x" onClick={onClose} aria-label="Cerrar recorrido"><X size={16} /></button>
        </div>
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          {index > 0 ? (
            <button type="button" className="tour-btn" onClick={onPrev}><ArrowLeft size={15} /> Anterior</button>
          ) : (
            <button type="button" className="tour-btn tour-btn-ghost" onClick={onSkip}>Omitir</button>
          )}
          <button type="button" className="tour-btn tour-btn-primary" onClick={onNext}>
            {last ? "Listo" : "Siguiente"} {!last && <ArrowRight size={15} />}
          </button>
        </div>
        {onSkipAll && (
          <button type="button" className="tour-skip-all" onClick={onSkipAll}>Omitir todos los recorridos</button>
        )}
      </div>
    </div>,
    document.body,
  );
}
